import { useEffect, useState } from "react";
import { Navigate, PathRouteProps, useLocation } from "react-router-dom";
import { ROUTES } from "../../constants/routes";
import { hasValidSession, isAccessTokenExpired, refreshAccessToken } from "../../services/tokenRefreshService";
import Spinner from "./Spinner";

interface Props extends PathRouteProps {
    children: JSX.Element;
    redirectRoute?: string;
}

type GuardState = "loading" | "allowed" | "denied";

function getInitialGuardState(): GuardState {
    if (!hasValidSession()) {
        return "denied";
    }

    if (!isAccessTokenExpired()) {
        return "allowed";
    }

    return "loading";
}

export const GuardRoute = ({ children, redirectRoute = ROUTES.auth.login }: Props) => {
    const location = useLocation();
    const [state, setState] = useState<GuardState>(getInitialGuardState);

    useEffect(() => {
        let cancelled = false;

        const ensureSession = async () => {
            if (!hasValidSession()) {
                if (!cancelled) {
                    setState("denied");
                }
                return;
            }

            if (!isAccessTokenExpired()) {
                if (!cancelled) {
                    setState("allowed");
                }
                return;
            }

            if (!cancelled) {
                setState("loading");
            }

            try {
                await refreshAccessToken();
                if (!cancelled) {
                    setState("allowed");
                }
            } catch {
                if (!cancelled) {
                    setState("denied");
                }
            }
        };

        ensureSession();

        return () => {
            cancelled = true;
        };
    }, [location.pathname]);

    if (state === "loading") {
        return <Spinner />;
    }

    if (state === "denied") {
        const redirectUrl = `${redirectRoute}?returnUrl=${encodeURIComponent(location.pathname + location.search)}`;
        return <Navigate to={redirectUrl} replace />;
    }

    return children;
};
